import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import MotoringClaimForm from '@acorn-portal/shared/src/components/forms/MotoringClaimsForm';
import MotoringConvictionsForm from '@acorn-portal/shared/src/components/forms/MotoringConvictionsForm';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { generateDate, isEmployed, isStudent } from '@acorn-portal/shared/src/utils';
import { DISPLAY_DATE, MIN_AGE } from '@acorn-portal/shared/src/utils/constants';
import { ADDITIONAL_DRIVER_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { convertToJsDate, DefaultDateObject } from '@acorn-portal/shared/src/utils/formDateValueUtils';
import {
	CONVICTIONS,
	LICENCE_OBTAINED_MODAL,
	SPECIAL_TERMS,
	TAXI_VEHICLE_USE,
	UK_RESIDENCY_MODAL,
} from '@acorn-portal/shared/src/utils/modalText';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { differenceInYears, format, isAfter, sub } from 'date-fns';
import { Form, Formik, FormikConfig, FormikHelpers, FormikProps, getIn } from 'formik';
import { FC, useMemo, useState } from 'react';
import { DriverFormValues } from '../../../utils/initialValues/get-driver-form-values';
import DriverLicenceField from '../../inputs/DriverLicenceField';
import { ExtraDriverField } from '../types';
import getInitialDrivingLicenceNumber from '@acorn-portal/shared/src/utils/get-initial-driving-licence-number';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export type SubmitText = 'Add' | 'Edit' | 'Save';
type DLNStatus = 'unlocked' | 'partial-lock';
export interface DriverFormProps {
	initialValues: DriverFormValues;
	onSubmit: (item: DriverFormValues) => void;
	onCancel: () => void;
	schema: FormikConfig<DriverFormValues>['validationSchema'];
	submitText: SubmitText;
	motoringClaimsYears: number;
	introText?: string;
	extraFields?: ExtraDriverField[];
}

// This is the full set of questions, usually for adding or editing an additional driver
const DriverForm: FC<DriverFormProps> = ({
	initialValues,
	onSubmit,
	onCancel,
	schema,
	submitText,
	motoringClaimsYears,
	introText,
	extraFields = [],
}) => {
	const lists = useAppSelector((redux) => redux.referenceData);
	const theme = useTheme();
	const [dlnStatus, setDlnStatus] = useState<DLNStatus>('unlocked'); // will be applied to the first two DLN fields
	const [formMaritalStatus, setFormMaritalStatus] = useState(''); //manage relationship to proposer select options based on marital status
	const relationshipProposerList = useMemo(
		() =>
			lists.relationshipToProposerTypes?.filter((relationship) =>
				formMaritalStatus === 'S' ? relationship.value !== 'S' : relationship,
			),
		[formMaritalStatus, lists.relationshipToProposerTypes],
	);

	// Helper to update DLN fields when title, last name or DOB changes
	const handleDLNUpdate = (formik: FormikProps<DriverFormValues>, changedField: Partial<DriverFormValues>) => {
		const title = changedField.title ?? formik.values.title;
		const lastNames = changedField.lastNames ?? formik.values.lastNames;
		const dob = changedField.dateOfBirth ?? formik.values.dateOfBirth;
		const updatedDLN = getInitialDrivingLicenceNumber({ title, lastNames, dob: convertToJsDate(dob) });
		formik.setFieldValue('licenceNumber', updatedDLN);
		if (updatedDLN['licenceField-1'] !== '' && updatedDLN['licenceField-2'] !== '') setDlnStatus('partial-lock');
		else setDlnStatus('unlocked');
	};

	const handleSubmit = (formValues: DriverFormValues, formikHelpers: FormikHelpers<DriverFormValues>) => {
		onSubmit(formValues);
		formikHelpers.resetForm();
		window.scrollTo({
			top: 100,
			left: 100,
			behavior: 'smooth',
		});
	};

	return (
		<Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
			{(formik: FormikProps<DriverFormValues>) => {
				return (
					<Box
						sx={(theme) => ({
							py: 2,
							px: 1,
							border: `1px solid ${theme.palette.divider}`,
							borderRadius: 2,
						})}
					>
						<Form>
							<FormikLogErrors />
							{introText && (
								<Typography fontSize={16} fontWeight='bold' align='center' paddingBottom={3}>
									{introText}
								</Typography>
							)}
							<SelectFormField
								label='Title'
								name='title'
								id='title'
								value={formik.values.title}
								onChange={(value) => {
									formik.setFieldValue('title', value);
									handleDLNUpdate(formik, { title: value });
								}}
								options={lists.titlesList as IOptions[]}
								error={formik.touched.title && Boolean(formik.errors.title)}
								helperText={formik.touched.title && formik.errors.title}
							/>
							<FormTextField
								id='firstName'
								name='firstName'
								label='First Name'
								value={formik.values.firstName}
								onChange={formik.handleChange}
								error={formik.touched.firstName && Boolean(formik.errors.firstName)}
								helperText={formik.touched.firstName && formik.errors.firstName}
							/>
							<FormTextField
								id='lastNames'
								name='lastNames'
								label='Surname'
								value={formik.values.lastNames}
								onChange={(e) => {
									formik.setFieldValue('lastNames', e.currentTarget.value);
									handleDLNUpdate(formik, { lastNames: e.currentTarget.value });
								}}
								error={formik.touched.lastNames && Boolean(formik.errors.lastNames)}
								helperText={formik.touched.lastNames && formik.errors.lastNames}
							/>
							<DatePickerField
								id='dateOfBirth'
								name='dateOfBirth'
								onChange={(value) => {
									formik.setFieldValue('dateOfBirth', value);
									handleDLNUpdate(formik, { dateOfBirth: value });
								}}
								label='Their date of birth'
								value={formik.values.dateOfBirth}
								error={
									(formik.touched.dateOfBirth?.day && Boolean(formik.errors.dateOfBirth?.day)) ||
									(formik.touched.dateOfBirth?.month && Boolean(formik.errors.dateOfBirth?.month)) ||
									(formik.touched.dateOfBirth?.year && Boolean(formik.errors.dateOfBirth?.year))
								}
								helperText={
									(formik.touched.dateOfBirth?.day && formik.errors.dateOfBirth?.day) ||
									(formik.touched.dateOfBirth?.month && formik.errors.dateOfBirth?.month) ||
									(formik.touched.dateOfBirth?.year && formik.errors.dateOfBirth?.year)
								}
							/>

							<SelectFormField
								label='Marital Status'
								name='maritalStatus'
								id='maritalStatus'
								value={formik.values.maritalStatus}
								onChange={(value) => {
									formik.setFieldValue('maritalStatus', value);
									setFormMaritalStatus(value);
								}}
								options={lists.maritalStatusList as IOptions[]}
								error={formik.touched.maritalStatus && Boolean(formik.errors.maritalStatus)}
								helperText={formik.touched.maritalStatus && formik.errors.maritalStatus}
							/>
							<SelectFormField
								label='Relationship to proposer'
								name='relationshipToProposer'
								id='relationshipToProposer'
								value={formik.values.relationshipToProposer}
								onChange={(value) => formik.setFieldValue('relationshipToProposer', value)}
								options={relationshipProposerList as IOptions[]}
								additionalDriver
								error={formik.touched.relationshipToProposer && Boolean(formik.errors.relationshipToProposer)}
								helperText={formik.touched.relationshipToProposer && formik.errors.relationshipToProposer}
							/>
							<RadioGroupButton
								label='Have they lived in the UK since Birth?'
								name='isResidentFromBirth'
								id='ukResidenceSince'
								value={formik.values.isResidentFromBirth}
								onChange={(value) => {
									formik.setFieldValue('isResidentFromBirth', value);
									formik.setFieldValue('ukResidenceSince', value === 'yes' ? undefined : DefaultDateObject);
								}}
								error={formik.touched.isResidentFromBirth && Boolean(formik.errors.isResidentFromBirth)}
								helperText={formik.touched.isResidentFromBirth && formik.errors.isResidentFromBirth}
							/>
							<Collapse in={formik.values.isResidentFromBirth === 'no'}>
								{formik.values.isResidentFromBirth === 'no' && (
									<DatePickerField
										id='ukResidenceSince'
										name='ukResidenceSince'
										onChange={(value) => formik.setFieldValue('ukResidenceSince', value)}
										label='What date did their UK residency begin?'
										value={formik.values.ukResidenceSince}
										error={['day', 'month', 'year'].some(
											(part) =>
												getIn(formik.touched.ukResidenceSince, part) &&
												Boolean(getIn(formik.errors.ukResidenceSince, part)),
										)}
										helperText={['day', 'month', 'year']
											.map(
												(part) =>
													getIn(formik.touched.ukResidenceSince, part) && getIn(formik.errors.ukResidenceSince, part),
											)
											.find(Boolean)}
										modal={{
											title: UK_RESIDENCY_MODAL.header,
											description: UK_RESIDENCY_MODAL.text,
										}}
									/>
								)}
							</Collapse>
							<ToggleButtonFormField
								label='What is their employment status?'
								name='employmentType'
								id='employmentType'
								value={formik.values.employmentType}
								onChange={(value) => {
									formik.setFieldValue('employmentType', value);
								}}
								options={lists.employmentStatusTypes as IOptions[]}
								error={formik.touched.employmentType && Boolean(formik.errors.employmentType)}
								helperText={formik.touched.employmentType && formik.errors.employmentType}
							/>
							<RadioGroupButton
								label='Is this occupation full time?'
								name='employmentStatus'
								id='employmentStatus'
								value={formik.values.employmentStatus}
								onChange={(value) => {
									formik.setFieldValue('employmentStatus', value, false);
								}}
								error={formik.touched.employmentStatus && Boolean(formik.errors.employmentStatus)}
								helperText={formik.touched.employmentStatus && formik.errors.employmentStatus}
							/>
							<Collapse in={isStudent(formik.values.employmentType)}>
								{isStudent(formik.values.employmentType) && (
									<AutoCompleteFormField
										label='What is their education type?'
										name='occupation'
										id='occupation'
										value={formik.values.occupation}
										onChange={(value) => formik.setFieldValue('occupation', value)}
										options={lists.studentOccupationTypes}
										error={formik.touched.occupation && Boolean(formik.errors.occupation)}
										helperText={formik.touched.occupation && formik.errors.occupation}
									/>
								)}
							</Collapse>
							<Collapse in={isEmployed(formik.values.employmentType)}>
								{isEmployed(formik.values.employmentType) && (
									<>
										<AutoCompleteFormField
											label='What is their main occupation?'
											name='occupation'
											id='occupation'
											value={formik.values.occupation}
											onChange={(value) => formik.setFieldValue('occupation', value)}
											options={lists.occupationTypes}
											error={formik.touched.occupation && Boolean(formik.errors.occupation)}
											helperText={formik.touched.occupation && formik.errors.occupation}
										/>
										<AutoCompleteFormField
											label='What type of industry is this?'
											name='industry'
											id='industry'
											value={formik.values.industry}
											onChange={(value) => formik.setFieldValue('industry', value)}
											options={lists.industryTypes}
											error={formik.touched.industry && Boolean(formik.errors.industry)}
											helperText={formik.touched.industry && formik.errors.industry}
										/>
									</>
								)}
							</Collapse>
							<RadioGroupButton
								label='Do they have another occupation?'
								name='otherEmployment'
								id='otherEmployment'
								value={formik.values.otherEmployment}
								onChange={(value) => {
									formik.setFieldValue('otherEmployment', value);
									formik.setFieldValue('otherOccupation', '');
									formik.setFieldValue('otherIndustry', '');
									formik.setFieldValue('otherEmploymentStatus', '');
									formik.setFieldValue('otherEmploymentType', '');
								}}
								error={formik.touched.otherEmployment && Boolean(formik.errors.otherEmployment)}
								helperText={formik.touched.otherEmployment && formik.errors.otherEmployment}
							/>
							<Collapse in={formik.values.otherEmployment === 'yes'}>
								{formik.values.otherEmployment === 'yes' && (
									<>
										<ToggleButtonFormField
											label='What is their employment status?'
											name='otherEmploymentType'
											id='otherEmploymentType'
											value={formik.values.otherEmploymentType}
											onChange={(value) => {
												formik.setFieldValue('otherEmploymentType', value);
											}}
											options={lists.employmentStatusTypes as IOptions[]}
											error={formik.touched.otherEmploymentType && Boolean(formik.errors.otherEmploymentType)}
											helperText={formik.touched.otherEmploymentType && formik.errors.otherEmploymentType}
										/>
										<RadioGroupButton
											label='Is this occupation full time?'
											name='otherEmploymentStatus'
											id='otherEmploymentStatus'
											value={formik.values.otherEmploymentStatus}
											onChange={(value) => {
												formik.setFieldValue('otherEmploymentStatus', value, false);
											}}
											error={formik.touched.otherEmploymentStatus && Boolean(formik.errors.otherEmploymentStatus)}
											helperText={formik.touched.otherEmploymentStatus && formik.errors.otherEmploymentStatus}
										/>
										<Collapse in={isStudent(formik.values.otherEmploymentType)}>
											{isStudent(formik.values.otherEmploymentType) && (
												<AutoCompleteFormField
													label='What is their education type?'
													name='otherOccupation'
													id='otherOccupation'
													value={formik.values.otherOccupation}
													onChange={(value) => formik.setFieldValue('otherOccupation', value)}
													options={lists.studentOccupationTypes}
													error={formik.touched.otherOccupation && Boolean(formik.errors.otherOccupation)}
													helperText={formik.touched.otherOccupation && formik.errors.otherOccupation}
												/>
											)}
										</Collapse>
										<Collapse in={isEmployed(formik.values.otherEmploymentType)}>
											{isEmployed(formik.values.otherEmploymentType) && (
												<>
													<AutoCompleteFormField
														label='What is their other/secondary occupation?'
														name='otherOccupation'
														id='otherOccupation'
														value={formik.values.otherOccupation}
														onChange={(value) => formik.setFieldValue('otherOccupation', value)}
														options={lists.occupationTypes}
														error={formik.touched.otherOccupation && Boolean(formik.errors.otherOccupation)}
														helperText={formik.touched.otherOccupation && formik.errors.otherOccupation}
													/>
													<AutoCompleteFormField
														label='What type of industry is this other/secondary occupation?'
														name='otherIndustry'
														id='otherIndustry'
														value={formik.values.otherIndustry}
														onChange={(value) => formik.setFieldValue('otherIndustry', value)}
														options={lists.industryTypes}
														error={formik.touched.otherIndustry && Boolean(formik.errors.otherIndustry)}
														helperText={formik.touched.otherIndustry && formik.errors.otherIndustry}
													/>
												</>
											)}
										</Collapse>
									</>
								)}
							</Collapse>
							<AutoCompleteFormField
								label='Licence Type'
								name='licenceType'
								id='licenceType'
								value={formik.values.licenceType}
								onChange={(value) => formik.setFieldValue('licenceType', value)}
								options={lists.licenceTypes as IOptions[]}
								error={formik.touched.licenceType && Boolean(formik.errors.licenceType)}
								helperText={formik.touched.licenceType && formik.errors.licenceType}
							/>
							{(formik.values.licenceType === 'F' ||
								formik.values.licenceType === 'P' ||
								formik.values.licenceType === 'X' ||
								formik.values.licenceType === '5') && (
								<DriverLicenceField name='licenceNumber' partialLock={dlnStatus === 'partial-lock'} />
							)}
							<DatePickerField
								id='licenceObtained'
								name='licenceObtained'
								onChange={(value) => {
									formik.setFieldValue('licenceObtained', value);
								}}
								label='Date current licence obtained'
								value={formik.values.licenceObtained}
								error={
									(formik.touched.licenceObtained?.day && Boolean(formik.errors.licenceObtained?.day)) ||
									(formik.touched.licenceObtained?.month && Boolean(formik.errors.licenceObtained?.month)) ||
									(formik.touched.licenceObtained?.year && Boolean(formik.errors.licenceObtained?.year))
								}
								helperText={
									(formik.touched.licenceObtained?.day && formik.errors.licenceObtained?.day) ||
									(formik.touched.licenceObtained?.month && formik.errors.licenceObtained?.month) ||
									(formik.touched.licenceObtained?.year && formik.errors.licenceObtained?.year)
								}
								modal={{
									title: LICENCE_OBTAINED_MODAL.header,
									description: LICENCE_OBTAINED_MODAL.text,
								}}
							/>
							{extraFields.includes('driverUsage') && (
								<>
									<SelectFormField
										label='What will they be using the vehicle for?'
										name='driverUsage'
										id='driverUsage'
										value={formik.values.driverUsage}
										onChange={(value) => formik.setFieldValue('driverUsage', value)}
										options={
											(lists.vehicleUseOptions.filter(
												(option) =>
													(option.value as string) === '4' ||
													(option.value as string) === '15' ||
													(option.value as string) === '17',
											) as IOptions[]) || []
										}
										error={formik.touched.driverUsage && Boolean(formik.errors.driverUsage)}
										helperText={
											(formik.touched.driverUsage && formik.errors.driverUsage) ||
											(formik.values.driverUsage === '4' && ADDITIONAL_DRIVER_ERRORS.driverUsageSDP)
										}
										helperTextColour={theme.palette.error.main}
										modal={{
											title: TAXI_VEHICLE_USE.header,
											description: TAXI_VEHICLE_USE.text,
										}}
									/>

									{formik.values.driverUsage !== '4' &&
										formik.values.driverUsage !== '' &&
										extraFields.includes('badgePassedDate') && (
											<Collapse in={formik.values.driverUsage !== '4'}>
												<DatePickerField
													id='badgePassedDate'
													onChange={(value) => {
														formik.setFieldValue('badgePassedDate', value);
													}}
													label='When did they obtain their taxi badge?'
													value={formik.values.badgePassedDate}
													error={['day', 'month', 'year'].some(
														(part) =>
															getIn(formik.touched.badgePassedDate, part) &&
															Boolean(getIn(formik.errors.badgePassedDate, part)),
													)}
													helperText={['day', 'month', 'year']
														.map(
															(part) =>
																getIn(formik.touched.badgePassedDate, part) &&
																getIn(formik.errors.badgePassedDate, part),
														)
														.find(Boolean)}
												/>
											</Collapse>
										)}
								</>
							)}
							<RadioGroupButton
								label='Do they have any motoring convictions in the last 5 years?'
								name='hasMotoringConvictions'
								id='hasMotoringConvictions'
								value={formik.values.hasMotoringConvictions}
								onChange={(value) => {
									formik.setFieldValue('hasMotoringConvictions', value);
								}}
								error={
									formik.touched.hasMotoringConvictions &&
									(Boolean(formik.errors.hasMotoringConvictions) || Boolean(formik.errors.motoringConvictions))
								}
								helperText={
									formik.touched.hasMotoringConvictions &&
									(formik.errors.hasMotoringConvictions || formik.errors.motoringConvictions)
								}
							/>
							<Collapse in={formik.values.hasMotoringConvictions === 'yes'}>
								<MotoringConvictionsForm
									isAdditionalDriver={formik.values.relationshipToProposer !== 'P'}
									isDateAfterDOB={(date, formikHelpers, field) => {
										const d = generateDate(formik.values.dateOfBirth);
										if (d instanceof Date && Number.isNaN(d.getTime())) {
											formik.setErrors({
												dateOfBirth: {
													day: ADDITIONAL_DRIVER_ERRORS.dateOfBirth,
												},
											});
											formik.setFieldTouched('dateOfBirth.day', true, true);
											formikHelpers.setErrors({
												[field]: {
													day: 'Please select their date of birth above',
												},
											});
											const errorElement = document.getElementsByName('day')[0];
											if (errorElement) {
												errorElement.scrollIntoView({
													behavior: 'smooth',
													block: 'center',
												});
												errorElement.focus({ preventScroll: true });
											}
											return false;
										}
										if (differenceInYears(sub(new Date(), { years: MIN_AGE }), d) <= 0) {
											formik.setErrors({
												dateOfBirth: {
													day: `Their date of birth must be at least 16 years`,
												},
											});
											const errorElement = document.getElementsByName('day')[0];
											if (errorElement) {
												errorElement.scrollIntoView({
													behavior: 'smooth',
													block: 'center',
												});
												errorElement.focus({ preventScroll: true });
											}
											return false;
										}
										if (isAfter(d, date)) {
											formikHelpers.setErrors({
												[field]: {
													day: `Please select a date after ${format(d, DISPLAY_DATE)}`,
												},
											});
											return false;
										}
										return true;
									}}
									onChange={(values) => formik.setFieldValue('motoringConvictions', values)}
									data={formik.values.motoringConvictions}
									hasMotoringConvictions={formik.values.hasMotoringConvictions === 'yes'}
								/>
							</Collapse>
							<RadioGroupButton
								label={`Have they had any motoring accidents, claims or losses within the last ${motoringClaimsYears} years?`}
								name='hasMotoringClaims'
								id='hasMotoringClaims'
								value={formik.values.hasMotoringClaims}
								onChange={(value) => {
									formik.setFieldValue('hasMotoringClaims', value);
								}}
								error={
									formik.touched.hasMotoringClaims &&
									(Boolean(formik.errors.hasMotoringClaims) || Boolean(formik.errors.motoringClaims))
								}
								helperText={
									formik.touched.hasMotoringClaims && (formik.errors.hasMotoringClaims || formik.errors.motoringClaims)
								}
							/>
							<Collapse in={formik.values.hasMotoringClaims === 'yes'}>
								<MotoringClaimForm
									isAdditionalDriver={formik.values.relationshipToProposer !== 'P'}
									checkDateOfClaim={(dateOfClaim, childFormikHelper) => {
										const d = generateDate(formik.values.dateOfBirth);
										if (d instanceof Date && Number.isNaN(d.getTime())) {
											formik.setErrors({
												dateOfBirth: {
													day: ADDITIONAL_DRIVER_ERRORS.dateOfBirth,
												},
											});
											formik.setFieldTouched('dateOfBirth.day', true);
											childFormikHelper.setErrors({
												dateOfClaim: {
													day: 'Please select their date of birth above',
												},
											});
											const errorElement = document.getElementsByName('day')[0];
											if (errorElement) {
												errorElement.scrollIntoView({
													behavior: 'smooth',
													block: 'center',
												});
												errorElement.focus({ preventScroll: true });
											}
											return true;
										}
										if (differenceInYears(sub(new Date(), { years: MIN_AGE }), d) <= 0) {
											formik.setErrors({
												dateOfBirth: {
													day: `Their date of birth must be at least 16 years`,
												},
											});
											const errorElement = document.getElementsByName('day')[0];
											if (errorElement) {
												errorElement.scrollIntoView({
													behavior: 'smooth',
													block: 'center',
												});
												errorElement.focus({ preventScroll: true });
											}
											return true;
										}

										if (isAfter(d, dateOfClaim)) {
											childFormikHelper.setErrors({
												dateOfClaim: {
													day: `Please select a date after ${format(d, DISPLAY_DATE)}`,
												},
											});
											return true;
										}
										return false;
									}}
									data={formik.values.motoringClaims}
									hasMotoringClaims={formik.values.hasMotoringClaims === 'yes'}
									onChange={(value) => formik.setFieldValue('motoringClaims', value)}
								/>
							</Collapse>
							<RadioGroupButton
								label='Do they have any unspent criminal convictions?'
								name='hasCriminalConvictions'
								id='hasCriminalConvictions'
								value={formik.values.hasCriminalConvictions}
								onChange={(value) => {
									formik.setFieldValue('hasCriminalConvictions', value);
								}}
								error={formik.touched.hasCriminalConvictions && Boolean(formik.errors.hasCriminalConvictions)}
								helperText={formik.touched.hasCriminalConvictions && formik.errors.hasCriminalConvictions}
								modal={{
									title: CONVICTIONS.header,
									description: CONVICTIONS.text,
								}}
							/>
							<RadioGroupButton
								label='Have they ever had an insurance policy declined, cancelled, voided or had any special terms imposed?'
								name='hasBeenRefusedCover'
								id='hasBeenRefusedCover'
								value={formik.values.hasBeenRefusedCover}
								onChange={(value) => {
									formik.setFieldValue('hasBeenRefusedCover', value);
								}}
								error={formik.touched.hasBeenRefusedCover && Boolean(formik.errors.hasBeenRefusedCover)}
								helperText={formik.touched.hasBeenRefusedCover && formik.errors.hasBeenRefusedCover}
								modal={{
									title: SPECIAL_TERMS.header,
									description: SPECIAL_TERMS.text,
								}}
							/>
							<Box
								display='flex'
								justifyContent='flex-end'
								sx={(theme) => ({
									px: 2,
									[theme.breakpoints.down('md')]: {
										pr: 'unset',
									},
								})}
							>
								<Button
									sx={(theme) => ({
										my: 2,
										mr: 'auto',
										minWidth: '10%',
										[theme.breakpoints.down('md')]: {
											width: '50%',
										},
									})}
									variant='text'
									color='secondary'
									onClick={() => {
										onCancel();
										formik.resetForm();
										window.scrollTo({
											top: 100,
											left: 100,
											behavior: 'smooth',
										});
									}}
								>
									Cancel
								</Button>
								<Button
									sx={(theme) => ({
										my: 2,
										ml: 'auto',
										minWidth: '100px',
										[theme.breakpoints.down('md')]: {
											width: '50%',
										},
									})}
									variant='contained'
									color='primary'
									type='submit'
								>
									{submitText}
								</Button>
							</Box>
						</Form>
					</Box>
				);
			}}
		</Formik>
	);
};

export default DriverForm;
